/*
 * File: Store.js
 * Project: mp-mobile-app
 *
 * Created by Rob Rothe
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Class to statically persist data needed for orders
 */

import Api from "../Api";
import References from "services/references/References";
import Users from "services/users/Users";
import PatientApi from "services/patients/store/Api";
import DataForFormSelect from "utils/DataForFormSelect";
import { flow, getEnv, types } from "mobx-state-tree";

const OrderType = types.model("OrderType", {
  type: types.string,
  typeId: types.number
});

const OrderStatus = types.model("OrderStatus", {
  orderStatus: types.string,
  orderStatusId: types.number
});

const OrderIncident = types.model("OrderIncident", {
  id: types.string,
  name: types.string
});

const Store = types
  .model("OrderReferences", {
    types: types.maybeNull(types.array(OrderType)),
    statuses: types.maybeNull(types.array(OrderStatus)),
    incidents: types.maybeNull(types.array(OrderIncident)),
    incidentPatientId: types.maybeNull(types.number)
  })
  .actions(self => ({
    /**
     * Returns order types from the API
     */
    getTypes: flow(function*() {
      if (!self.types) self.types = yield Api.orderTypes();
      return self.types;
    }),

    /**
     * Returns order statuses from the API
     */
    getStatuses: flow(function*() {
      if (!self.statuses) self.statuses = yield Api.orderStatuses();
      return self.statuses;
    }),

    getIncidents: flow(function*(patientId) {
      if (self.incidentPatientId !== patientId) {
        self.incidentPatientId = patientId;
        let incidentsData = yield PatientApi.incidents(patientId);
        self.incidents = [
          { id: "0", name: "None" },
          ...DataForFormSelect(incidentsData, "incidentId", "incidentName")
        ];
      }
      return self.incidents;
    })
  }))
  .views(self => ({
    /**
     * Returns the open status ids in an array
     */
    getOpenStatusIds: () => {
      return [1, 7, 8];
    },

    providerById: providerId => {
      return (
        getEnv(self)
          .storeManager.getStore(Users)
          .providerById(providerId) || {}
      );
    },

    userById: userId => {
      return (
        getEnv(self)
          .storeManager.getStore(Users)
          .userById(userId) || {}
      );
    },

    incidentById: incidentId => {
      let incident = {};
      if (self.incidents) {
        incident = self.incidents.find(
          i => Number(i.id) === Number(incidentId)
        );
      }

      return incident ? incident.name : "";
    },

    /**
     * Gets the status name by id
     *
     * @param {Number} statusId
     * @return {String}
     */
    statusById: statusId => {
      if (self.statuses && statusId) {
        let status = self.statuses.find(s => s.orderStatusId === statusId);
        return status.orderStatus || "";
      }
      return "";
    },

    /**
     * Gets the type name by id
     *
     * @param {Number} typeId
     * @return {String}
     */
    typeById: typeId => {
      if (self.types && typeId) {
        let type = self.types.find(t => t.typeId === typeId);
        return type.type || "";
      }
      return "";
    },

    /**
     * Gets office name by id
     *
     * @param {Number} typeId
     * @return {Object}
     */
    officeById: officeId => {
      return (
        getEnv(self)
          .storeManager.getStore(References)
          .officeById(officeId) || {}
      );
    }
  }));

export default Store;

export const preloadOrderReferences = async storeManager => {
  const orderReferences = storeManager.getStore(Store);
  await orderReferences.getTypes();
  await orderReferences.getStatuses();
};
